// Flag picker for the cave's admin forms: the 13 clan flags, every uploaded
// flag (GET /api/flags) and an "Upload flag" tile (managers only, POST
// /api/flags). An upload is picked as soon as it lands.
import { useState } from 'react';
import type { ChangeEvent } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { getFlagList, uploadFlag } from '../api.js';
import { CLAN_FLAGS } from '../site/pages/profileEditing.js';
import styles from './FlagPicker.module.css';

export function FlagPicker({ value, onChange }: { value: string; onChange: (flag: string) => void }) {
  const queryClient = useQueryClient();
  const flagsQuery = useQuery({ queryKey: ['flags'], queryFn: getFlagList });
  const [error, setError] = useState<string | null>(null);

  const uploadMutation = useMutation({
    mutationFn: uploadFlag,
    onSuccess: (res) => {
      setError(null);
      queryClient.invalidateQueries({ queryKey: ['flags'] });
      queryClient.invalidateQueries({ queryKey: ['uploaded-flags'] });
      onChange(res.flag);
    },
    onError: (err) => setError(err instanceof Error ? err.message : 'Upload failed.'),
  });

  function handleFileChange(event: ChangeEvent<HTMLInputElement>) {
    const file = event.target.files?.[0];
    event.target.value = ''; // lets the same file be picked again after an error
    if (!file) return;
    uploadMutation.mutate(file);
  }

  const uploaded = flagsQuery.data?.flags ?? [];
  const options = [...CLAN_FLAGS, ...uploaded.filter((f) => !CLAN_FLAGS.includes(f))];
  // the current flag stays visible even before the list has loaded (or after it was removed)
  if (value && !options.includes(value)) options.push(value);

  return (
    <div className={styles.picker}>
      <div className={styles.grid} role="radiogroup" aria-label="Flag">
        {options.map((flag) => (
          <button
            key={flag}
            type="button"
            role="radio"
            aria-checked={value === flag}
            className={value === flag ? `${styles.tile} ${styles.tileSelected}` : styles.tile}
            onClick={() => onChange(flag)}
          >
            <img src={flag} alt="" />
          </button>
        ))}
        <label className={styles.uploadTile}>
          {uploadMutation.isPending ? 'Uploading…' : 'Upload flag'}
          <input
            type="file"
            accept="image/png,image/webp,image/jpeg"
            className={styles.uploadInput}
            onChange={handleFileChange}
            disabled={uploadMutation.isPending}
          />
        </label>
      </div>
      {error && <p className={styles.error}>{error}</p>}
    </div>
  );
}
